"use client";

import { useEffect, useRef, useState } from 'react';
import { notFound, useRouter } from 'next/navigation';
import Image from "next/image";
import { db } from '@/lib/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { DEFAULT_CLUB_COLOR, getClubColor, getContrastTextColor } from '@/lib/utils';
import { Hero } from "@/components/hero";
import { LeagueTable } from "@/components/league-table";
import { ClubTv } from "@/components/club-tv";
import { NewsSection } from "@/components/news-section";
import { ClubHeader } from "@/components/club-header";
import { ClubFooter } from "@/components/club-footer";
import { PartnerStripClient } from "@/components/partner-strip-client";
import { MatchResultsList, MatchSection } from "@/components/match-section";
import type { MatchDetails } from "@/types/match";
import { Loader2 } from "lucide-react";
import { ClubHomePattern2 } from "@/components/club-home-pattern2";

export default function ClubPageContent({
  clubId,
  initialClubInfo,
}: {
  clubId: string;
  initialClubInfo?: any | null;
}) {
    const [clubInfo, setClubInfo] = useState<any>(
      initialClubInfo ?? {
        news: [],
        latestResult: null,
        nextMatch: null,
        profile: { clubName: '' },
        videos: [],
        competitions: [],
      }
    );
    const [isLoading, setIsLoading] = useState(!initialClubInfo);
    const [error, setError] = useState<string | null>(null);
    const [homePanel, setHomePanel] = useState<'standings' | 'results'>('standings');
    const [selectedRoundIndex, setSelectedRoundIndex] = useState(0);
    const [rounds, setRounds] = useState<{ roundId: string; roundName: string }[]>([]);
    const router = useRouter();
    const routerRef = useRef(router);

    useEffect(() => {
        routerRef.current = router;
    }, [router]);

    useEffect(() => {
        if (clubId === 'admin') {
            notFound();
            return;
        }

        let cancelled = false;

        const runFullFetch = async () => {
            try {
                const fullRes = await fetch(`/api/club/${clubId}`, { cache: 'no-store' });
                if (!fullRes.ok) {
                    console.error(`Full club data HTTP error: ${fullRes.status}`);
                    setError("データの読み込みに失敗しました");
                    return;
                }
                const fullData = await fullRes.json();
                if (cancelled) return;
                setClubInfo(fullData);
                setError(null);
            } catch (fullErr) {
                console.error("Failed to fetch full club data:", fullErr);
                setError("データの読み込みに失敗しました");
            }
        };

        void runFullFetch();

        return () => {
            cancelled = true;
        };
    }, [clubId, initialClubInfo]);

    const homeBgColor = getClubColor(clubInfo);
    const rawHomeColorTheme =
      ((clubInfo.data?.homeColorTheme as string | undefined) ??
      (clubInfo.profile?.homeColorTheme as string | undefined) ??
      (clubInfo.homeColorTheme as string | undefined));
    const homeColorTheme = (rawHomeColorTheme === 'dark' || rawHomeColorTheme === 'light')
      ? rawHomeColorTheme
      : 'light';
    const clubColor = homeBgColor || DEFAULT_CLUB_COLOR;
    const contrastColor = getContrastTextColor(clubColor);
    const headerLayout = (clubInfo.profile?.headerLayout === 'center' || clubInfo.profile?.headerLayout === 'left')
      ? clubInfo.profile.headerLayout
      : 'left';
    const homeLayout = (clubInfo.profile?.homeLayout === 'pattern1' || clubInfo.profile?.homeLayout === 'pattern2')
      ? 'pattern2'
      : 'default';
    const isDarkHomeTheme = homeColorTheme === 'dark';
    const mainTextColor = homeBgColor ? contrastColor : (isDarkHomeTheme ? '#FFFFFF' : '#000000');
    const heroNewsLimit =
        (clubInfo.data?.heroNewsLimit as number | undefined) ??
        (clubInfo.profile?.heroNewsLimit as number | undefined) ??
        3;

    const heroNewsRaw = (clubInfo as any).heroNews || clubInfo.news || [];
    const listNewsRaw = clubInfo.news || [];

    console.log('[ClubPageContent] heroNewsRaw:', heroNewsRaw);
    console.log('[ClubPageContent] listNewsRaw:', listNewsRaw);
    console.log('[ClubPageContent] clubInfo.heroNews:', (clubInfo as any).heroNews);
    console.log('[ClubPageContent] clubInfo.news:', clubInfo.news);

    const heroNews = (Array.isArray(heroNewsRaw) ? heroNewsRaw : []).filter((x) => x && typeof x === "object");
    const listNews = (Array.isArray(listNewsRaw) ? listNewsRaw : []).filter((x) => x && typeof x === "object");

    console.log('[ClubPageContent] heroNews:', heroNews);
    console.log('[ClubPageContent] listNews:', listNews);
    const videos = clubInfo.videos || [];

    const mainTeamId = clubInfo.profile?.mainTeamId || null;
    const dataClubId = (clubInfo.profile?.clubUid as string | undefined) ?? (clubInfo.profile?.ownerUid as string | undefined) ?? clubId;
    const recentMatches = (clubInfo.recentMatches || []) as MatchDetails[];
    
    // Client-side fetch of all match data to avoid serialization issues
    const [clientAllRecentMatches, setClientAllRecentMatches] = useState<MatchDetails[]>([]);

    useEffect(() => {
      const fetchAllMatches = async () => {
        try {
          if (!clubId) return;
          
          const [competitionsSnap, teamsSnap] = await Promise.all([
            getDocs(collection(db, `clubs/${dataClubId}/competitions`)),
            getDocs(collection(db, `clubs/${dataClubId}/teams`)),
          ]);
          const teamsMap = new Map<string, { name: string; logoUrl?: string }>();
          teamsSnap.forEach((teamDoc) => {
            const teamData = teamDoc.data();
            teamsMap.set(teamDoc.id, { name: teamData.name || '', logoUrl: teamData.logoUrl });
          });
          
          const allMatches: MatchDetails[] = [];
          
          for (const compDoc of competitionsSnap.docs) {
            const competitionId = compDoc.id;
            const competitionData = compDoc.data();
            
            // Fetch all rounds for this competition
            const roundsSnap = await getDocs(collection(db, `clubs/${dataClubId}/competitions/${competitionId}/rounds`));
            
            for (const roundDoc of roundsSnap.docs) {
              const roundId = roundDoc.id;
              const roundData = roundDoc.data();
              
              // Fetch all matches for this round
              const matchesSnap = await getDocs(collection(db, `clubs/${dataClubId}/competitions/${competitionId}/rounds/${roundId}/matches`));
              
              matchesSnap.forEach((matchDoc) => {
                const matchData = matchDoc.data();
                const homeTeam = teamsMap.get(matchData.homeTeam);
                const awayTeam = teamsMap.get(matchData.awayTeam);
                if (matchData.scoreHome !== null && matchData.scoreAway !== null) {
                  allMatches.push({
                    id: matchDoc.id,
                    competitionId,
                    roundId,
                    homeTeam: matchData.homeTeam,
                    awayTeam: matchData.awayTeam,
                    homeTeamName: matchData.homeTeamName || homeTeam?.name || '不明',
                    awayTeamName: matchData.awayTeamName || awayTeam?.name || '不明',
                    competitionName: competitionData.name,
                    competitionLogoUrl: competitionData.logoUrl,
                    roundName: roundData.name,
                    homeTeamLogo: matchData.homeTeamLogo || homeTeam?.logoUrl,
                    awayTeamLogo: matchData.awayTeamLogo || awayTeam?.logoUrl,
                    matchDate: matchData.matchDate,
                    matchTime: matchData.matchTime,
                    scoreHome: matchData.scoreHome,
                    scoreAway: matchData.scoreAway,
                    pkScoreHome: matchData.pkScoreHome,
                    pkScoreAway: matchData.pkScoreAway,
                  } as MatchDetails);
                }
              });
            }
          }
          
          const friendlySnap = await getDocs(collection(db, `clubs/${dataClubId}/friendly_matches`));
          friendlySnap.forEach((matchDoc) => {
            const matchData = matchDoc.data();
            const homeTeam = teamsMap.get(matchData.homeTeam);
            const awayTeam = teamsMap.get(matchData.awayTeam);
            if (matchData.scoreHome !== null && matchData.scoreAway !== null) {
              const compId = matchData.competitionId === 'practice' ? 'practice' : 'friendly';
              allMatches.push({
                id: matchDoc.id,
                competitionId: compId,
                roundId: 'single',
                homeTeam: matchData.homeTeam,
                awayTeam: matchData.awayTeam,
                homeTeamName: matchData.homeTeamName || homeTeam?.name || '不明',
                awayTeamName: matchData.awayTeamName || awayTeam?.name || '不明',
                competitionName: matchData.competitionName || (compId === 'practice' ? '練習試合' : '親善試合'),
                competitionLogoUrl: matchData.competitionLogoUrl,
                roundName: typeof matchData.roundName === 'string' ? matchData.roundName : '',
                homeTeamLogo: matchData.homeTeamLogo || homeTeam?.logoUrl,
                awayTeamLogo: matchData.awayTeamLogo || awayTeam?.logoUrl,
                matchDate: matchData.matchDate,
                matchTime: matchData.matchTime,
                scoreHome: matchData.scoreHome,
                scoreAway: matchData.scoreAway,
                pkScoreHome: matchData.pkScoreHome,
                pkScoreAway: matchData.pkScoreAway,
              } as MatchDetails);
            }
          });

          allMatches.sort((a, b) => new Date(b.matchDate).getTime() - new Date(a.matchDate).getTime());
          
          setClientAllRecentMatches(allMatches);
        } catch (error) {
          console.error('Error fetching matches:', error);
        }
      };

      fetchAllMatches();
    }, [clubId, dataClubId, clubInfo.competitions]);

    const allRecentMatches = clientAllRecentMatches.length > 0 ? clientAllRecentMatches : (clubInfo.allRecentMatches || recentMatches) as MatchDetails[];

    useEffect(() => {
      if (allRecentMatches && allRecentMatches.length > 0) {
        const matches = allRecentMatches as MatchDetails[];
        const roundsMap = matches.reduce((map, match) => {
            if (!match) return map;
            // Group by roundName only to include matches from all competitions
            const key = match.roundName || '';
            if (!map.has(key)) {
              map.set(key, { roundId: match.roundId || '', roundName: match.roundName || '', latestDate: match.matchDate });
            } else {
              const current = map.get(key)!;
              const matchDate = new Date(match.matchDate).getTime();
              const currentLatestDate = new Date(current.latestDate).getTime();
              if (matchDate > currentLatestDate) {
                current.latestDate = match.matchDate;
                current.roundId = match.roundId || ''; // Use the latest match's roundId
              }
            }
            return map;
          }, new Map<string, { roundId: string; roundName: string; latestDate: string }>());
        const roundsList = Array.from(roundsMap.values())
          .sort((a, b) => new Date(a.latestDate).getTime() - new Date(b.latestDate).getTime())
          .map(({ roundId, roundName }) => ({ roundId, roundName }));
        setRounds(roundsList);

        const latestMatch = matches
          .filter((m) => m && m.scoreHome !== null && m.scoreAway !== null)
          .sort((a, b) => {
            const msA = new Date(a.matchDate).getTime();
            const msB = new Date(b.matchDate).getTime();
            return msB - msA;
          })[0];
        if (latestMatch) {
          const latestIndex = roundsList.findIndex(
            (r) => r.roundId === latestMatch.roundId && r.roundName === latestMatch.roundName
          );
          if (latestIndex !== -1) {
            setSelectedRoundIndex(latestIndex);
          }
        }
      } else {
        setRounds([]);
      }
    }, [allRecentMatches]);

    const renderHomePanelContent = () => {
      const hasLeagueCompetition = (clubInfo.competitions || []).some((c: any) => c?.format !== 'cup');
      const resultsList = (
        <MatchResultsList
          matches={allRecentMatches}
          clubSlug={clubId}
          rounds={rounds}
          selectedRoundIndex={selectedRoundIndex}
          onRoundChange={setSelectedRoundIndex}
          colorTheme={homeColorTheme}
        />
      );

      if (!hasLeagueCompetition) {
        return <div className="space-y-3">{resultsList}</div>;
      }

      return (
      <div className="space-y-3">
        <div className={`grid grid-cols-2 rounded-full p-1 text-sm font-bold ${isDarkHomeTheme ? 'bg-white/10 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
          <button
            type="button"
            onClick={() => setHomePanel('standings')}
            className={homePanel === 'standings' ? `rounded-full px-3 py-2 shadow-sm ${isDarkHomeTheme ? 'bg-[#101116] text-white' : 'bg-white text-gray-950'}` : `rounded-full px-3 py-2 ${isDarkHomeTheme ? 'hover:text-white' : 'hover:text-gray-900'}`}
          >
            順位表
          </button>
          <button
            type="button"
            onClick={() => setHomePanel('results')}
            className={homePanel === 'results' ? `rounded-full px-3 py-2 shadow-sm ${isDarkHomeTheme ? 'bg-[#101116] text-white' : 'bg-white text-gray-950'}` : `rounded-full px-3 py-2 ${isDarkHomeTheme ? 'hover:text-white' : 'hover:text-gray-900'}`}
          >
            試合結果
          </button>
        </div>
        {homePanel === 'standings' ? (
          <LeagueTable clubId={clubId} competitions={clubInfo.competitions || []} minCardOnMobile colorTheme={homeColorTheme} />
        ) : (
          resultsList
        )}
      </div>
      );
    };

    if (isLoading) {
      return (
        <main className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Image
              src="/favicon.png"
              alt="Loading"
              width={64}
              height={64}
              className="opacity-90 animate-pulse"
              priority
            />
            <p className="text-sm text-muted-foreground">読み込み中</p>
          </div>
        </main>
      );
    }

    if (error) {
      return (
        <main className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center px-4">
            <div className="text-red-500">
              <Loader2 className="w-12 h-12" />
            </div>
            <p className="text-lg font-semibold">エラーが発生しました</p>
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={() => {
                setError(null);
                setIsLoading(true);
                window.location.reload();
              }}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              再読み込み
            </button>
          </div>
        </main>
      );
    }

    if (homeLayout === 'pattern2') {
      return (
        <main
          className="min-h-screen bg-[#FAF9F7]"
          style={{ '--club-color': clubColor, '--club-text-color': contrastColor } as React.CSSProperties}
        >
          <ClubHeader
            clubId={clubId}
            clubName={clubInfo.profile?.clubName || ""}
            logoUrl={clubInfo.profile?.logoUrl || null}
            headerBackgroundColor={homeBgColor}
            headerLayout={headerLayout}
            snsLinks={clubInfo.profile?.snsLinks || {}}
          />
          <ClubHomePattern2
            clubId={clubId}
            clubName={clubInfo.profile?.clubName || ""}
            logoUrl={clubInfo.profile?.logoUrl || null}
            accentColor={clubColor}
            foundedYear={clubInfo.profile?.foundedYear || ""}
            stadiumPhotoUrl={clubInfo.profile?.stadiumPhotoUrl || clubInfo.data?.headerImageUrl || ""}
            heroNews={heroNews}
            news={listNews}
            videos={videos}
            recentMatches={recentMatches}
            upcomingMatches={(clubInfo as any).upcomingMatches || []}
            competitions={clubInfo.competitions || []}
            mainTeamId={clubInfo.profile?.mainTeamId || null}
            players={(clubInfo as any).players || []}
          />
          <PartnerStripClient clubId={clubId} />
          <ClubFooter
            clubId={clubId}
            clubName={clubInfo.profile?.clubName || ""}
            gameTeamUsage={Boolean((clubInfo as any).profile?.gameTeamUsage)}
            sponsors={clubInfo.profile?.sponsors || []}
            snsLinks={clubInfo.profile?.snsLinks || {}}
            legalPages={clubInfo.profile?.legalPages || []}
          />
        </main>
      );
    }

    return (
        <main
          className="min-h-screen"
          style={{
            backgroundColor: homeBgColor || (isDarkHomeTheme ? '#050506' : '#ffffff'),
            color: mainTextColor,
            '--club-color': clubColor,
            '--club-text-color': contrastColor,
          } as React.CSSProperties}
        >
            <ClubHeader
                clubId={clubId}
                clubName={clubInfo.profile?.clubName || ""}
                logoUrl={clubInfo.profile?.logoUrl || null}
                headerBackgroundColor={homeBgColor}
                headerLayout={headerLayout}
                snsLinks={clubInfo.profile?.snsLinks || {}}
            />
            <div className="md:hidden">
              <Hero news={heroNews} maxSlides={heroNewsLimit} isLoading={isLoading} />
            </div>

            <div className="hidden md:block">
              <Hero news={heroNews} maxSlides={heroNewsLimit} isLoading={isLoading} />
              <NewsSection news={listNews} clubId={clubId} fallbackLogoUrl={clubInfo.profile?.logoUrl || null} colorTheme={homeColorTheme} />
              <div className="container mx-auto px-4 pt-12 pb-8">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <MatchSection 
                      nextMatch={clubInfo.nextMatch} 
                      upcomingMatches={(clubInfo as any).upcomingMatches || []}
                      recentMatches={recentMatches}
                      mainTeamId={mainTeamId}
                      backgroundColor={homeBgColor || null}
                      clubSlug={clubId}
                      colorTheme={homeColorTheme}
                    />
                    {videos.length > 0 && <ClubTv videos={videos} clubId={clubId} />}
                  </div>
                  <div className="space-y-4">
                    <LeagueTable clubId={clubId} competitions={clubInfo.competitions || []} minCardOnMobile colorTheme={homeColorTheme} />
                    <MatchResultsList
                      matches={allRecentMatches}
                      clubSlug={clubId}
                      rounds={rounds}
                      selectedRoundIndex={selectedRoundIndex}
                      onRoundChange={setSelectedRoundIndex}
                      colorTheme={homeColorTheme}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <NewsSection news={listNews} clubId={clubId} fallbackLogoUrl={clubInfo.profile?.logoUrl || null} colorTheme={homeColorTheme} />
            </div>
            <div className="container mx-auto px-4 pt-12 pb-8 md:hidden">
                <div className="space-y-3">
                    <MatchSection 
                        nextMatch={clubInfo.nextMatch} 
                        upcomingMatches={(clubInfo as any).upcomingMatches || []}
                        recentMatches={recentMatches}
                        mainTeamId={mainTeamId}
                        backgroundColor={homeBgColor || null}
                        clubSlug={clubId}
                        colorTheme={homeColorTheme}
                    />
                    {renderHomePanelContent()}
                    {videos.length > 0 && <ClubTv videos={videos} clubId={clubId} />}
                </div>
            </div>
            <PartnerStripClient clubId={clubId} />
            <ClubFooter 
              clubId={clubId}
              clubName={clubInfo.profile?.clubName || ""}
              gameTeamUsage={Boolean((clubInfo as any).profile?.gameTeamUsage)}
              sponsors={clubInfo.profile?.sponsors || []} 
              snsLinks={clubInfo.profile?.snsLinks || {}} 
              legalPages={clubInfo.profile?.legalPages || []}
            />
        </main>
    );
}
